import { useEffect, useRef, useState } from "react";
import {
    Button, Input, ModalHeader, Modal, ModalFooter, Dropdown, DropdownMenu, DropdownItem,
    DropdownToggle, Label, Col, Row, FormGroup
} from 'reactstrap';
import { useDispatch, useSelector } from "react-redux";
import { Tooltip as ReactTooltip } from 'react-tooltip'
import { fetchEmailBody, fetchEmailSubject, markAsMailed } from "../../details/apiclient";
import { fetchEmailPrompts } from "../../login/apiclient";
import _ from "lodash";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { logEvent } from "../../justask/apiclient";
import { resetEmailBody, resetEmailSubject, updateBodyEventStreamStatus, updateSubjectEventStreamStatus } from "../../details/redux/reducer";

const LaunchEmailModal = (props) => {
    const firstPromptRun = useRef(true);
    const firstStyleRun = useRef(true);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const emailPrompts = useSelector((state) => state.settings.emailPrompts);
    const userSettings = useSelector((state) => state.user.user);
    const projectDetails = useSelector((state) => state.project);
    const settings = useSelector((state) => state.settings)
    const [dropDownOpen, setDropDownOpen] = useState({ promptDropdown: false });
    const [dropDownValuePrompt, setDropDownValuePrompt] = useState(emailPrompts?.find(prompt => prompt.is_default)?.prompt_id || (emailPrompts.length > 0 ? emailPrompts[0].prompt_id : null));
    const [to, setTo] = useState('');
    const [formal, setIsFormal] = useState(settings.preferences.default_writing_style ? settings.preferences.default_writing_style === 'formal' : true);
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [toError, setToError] = useState('');
    const [subjectError, setSubjectError] = useState('');
    const [bodyError, setBodyError] = useState('');
    const [projectEmail, setProjectEmail] = useState(false);
    const [companyEmail, setCompanyEmail] = useState(false);
    const [peopleEmail, setPeopleEmail] = useState(false);
    const [isPageLoading, setIsPageLoading] = useState(false);
    const [isSubjectLoading, setIsSubjectLoading] = useState(false);
    const [isBodyLoading, setIsBodyLoading] = useState(false);
    const skipPromptReset = useRef(false);
    const isOpenRef = useRef(props.isOpen);
    const selectedPromptRef = useRef(dropDownValuePrompt);
    isOpenRef.current = props.isOpen;
    selectedPromptRef.current = dropDownValuePrompt;

    useEffect(() => {
        setSubject("")
        setBody("")
        dispatch(resetEmailSubject(''))
        dispatch(resetEmailBody(''))

        if (props.objectType === 'person') {
            setTo(props.entity.person_email ? props.entity.person_email : '')
        } else {
            setTo(props.entity.company_email ? props.entity.company_email : '')
        }

        if (props.isOpen) {
            setIsPageLoading(true);
            setSubjectError("Streaming is in progress please wait...")
            setBodyError("Streaming is in progress please wait...")
            refreshPromptsAndGetEmailBody()
            getEmailSubject()
        }
    }, [props.isOpen])

    useEffect(() => {
        if (firstPromptRun.current || skipPromptReset.current) {
            firstPromptRun.current = false;
            skipPromptReset.current = false;
            return; // 🔥 skip first modal open and selection corrected after refreshing prompts
        }
        resetEmailPopup()
    }, [dropDownValuePrompt])

    // Prompts may have been added/deleted in another tab since this page loaded,
    // so reload them from the server before generating the body
    const refreshPromptsAndGetEmailBody = async () => {
        setIsBodyLoading(true);
        dispatch(updateBodyEventStreamStatus('in_progress'));
        const prompts = (await fetchEmailPrompts(dispatch, userSettings.user_id)) ?? emailPrompts
        if (!isOpenRef.current) return

        // Every open starts from the default prompt (else the first), like a fresh mount does
        const currentId = selectedPromptRef.current
        const promptId = prompts.find(prompt => prompt.is_default)?.prompt_id
            ?? prompts[0]?.prompt_id
            ?? null
        if (promptId !== currentId) {
            skipPromptReset.current = true
            setDropDownValuePrompt(promptId)
        }
        getEmailBody(prompts.find(prompt => prompt.prompt_id === promptId)?.text || '')
    }

    useEffect(() => {
        if (firstStyleRun.current) {
            firstStyleRun.current = false;
            return; // 🔥 skip first modal open
        }
        resetEmailPopup()
    }, [formal])

    const resetEmailPopup = () => {
        setBody('')
        setSubject('')
        setSubjectError("Streaming is in progress please wait...")
        setBodyError("Streaming is in progress please wait...")
        getEmailSubject()
        getEmailBody()
        dispatch(resetEmailSubject(''))
        dispatch(resetEmailBody(''))
    }

    useEffect(() => {
        setBodyError(projectDetails.bodyEventStreamStatus === 'idle' ? "" : projectDetails.bodyEventStreamStatus === 'error' ? "We couldn’t generate your email because part of our system is temporarily unavailable. Please try again later." 
            : "Streaming is in progress please wait...")
        setIsBodyLoading(['idle', 'error'].includes(projectDetails.bodyEventStreamStatus) ? false : true);
        if (isPageLoading) {
            logEvents(160, 'AI Email Created')
            setIsPageLoading(false);
        }
    }, [projectDetails.bodyEventStreamStatus])

    useEffect(() => {
        setSubjectError(projectDetails.subjectEventStreamStatus === 'idle' ? "" : projectDetails.subjectEventStreamStatus === 'error' ? "We couldn’t generate your email because part of our system is temporarily unavailable. Please try again later." 
            : "Streaming is in progress please wait...")
        setIsSubjectLoading(['idle', 'error'].includes(projectDetails.subjectEventStreamStatus) ? false : true);
    }, [projectDetails.subjectEventStreamStatus])

    useEffect(() => {
        setSubject(projectDetails.emailSubject ? projectDetails.emailSubject : '')
    }, [projectDetails.emailSubject])

    useEffect(() => {
        if (subject === '' && projectDetails.emailSubject) {
            setSubject(projectDetails.emailSubject)
            setSubjectError("")
        }

        if (projectDetails.emailBody) {
            setBody(projectDetails.emailBody)
        }
    }, [projectDetails.emailBody])

    const toggleModal = (e) => {
        setSubject("");
        setBody("");
        setSubjectError("");
        setBodyError('');
        setToError('');
        setProjectEmail(false);
        setCompanyEmail(false);
        setPeopleEmail(false);
        dispatch(resetEmailSubject(''))
        dispatch(resetEmailBody(''))
        props.toggleModal(e)
    }

    const toggleDropdown = (value) => {
        setDropDownOpen({ ...dropDownOpen, [value]: !dropDownOpen[value] })
    }

    const handleDropdownLabel = (type, value) => {
        if (type === 'promptDropdown') {
            setDropDownValuePrompt(value.prompt_id)
        }
    }

    const getEmailSubject = () => {
        setIsSubjectLoading(true);
        dispatch(updateSubjectEventStreamStatus('in_progress'));
        const emailSubjectPayload = {
            project_title: projectDetails.detailsPage.project_title,
            project_scheme: projectDetails.detailsPage.project_scheme,
            tone: formal ? 'formal' : 'casual',
            user_id: userSettings.user_id
        }

        if (!_.isEmpty(props.entity) && props.isOpen) {
            fetchEmailSubject(emailSubjectPayload, dispatch)
        }
    }

    const getEmailBody = (senderPrompt = emailPrompts.find(prompt => prompt.prompt_id === dropDownValuePrompt)?.text || '') => {
        setIsBodyLoading(true);
        dispatch(updateBodyEventStreamStatus('in_progress'));
        const emailBodyPayload = {
            sender_name: userSettings.first_name + ' ' + userSettings.last_name,
            sender_company: userSettings.company_name,
            sender_prompt: senderPrompt,
            company_name: props.entity.company_name ? props.entity.company_name : props.company.companyName,
            company_role: props.entity.role_name ? props.entity.role_name : props.company.companyRole,
            project_title: projectDetails.detailsPage.project_title,
            project_scheme: projectDetails.detailsPage.project_scheme,
            project_materials: projectDetails.detailsPage.project_materials_text,
            project_site1: projectDetails.detailsPage.project_site1,
            project_site2: projectDetails.detailsPage.project_site2,
            project_site3: projectDetails.detailsPage.project_site3,
            project_site4: projectDetails.detailsPage.project_site4,
            project_postcode: projectDetails.detailsPage.project_postcode,
            tone: formal ? 'formal' : 'casual',
            user_id: userSettings.user_id
        };
        if (props.objectType === 'person') {
            emailBodyPayload['person_name'] = props.entity.person_first_name + ' ' + props.entity.person_last_name
        }

        if (!_.isEmpty(props.entity) && props.isOpen) {
            fetchEmailBody(emailBodyPayload, dispatch)
        }
    }

    const handleInputChange = (event) => {
        if (event.target.name === 'subject') {
            setSubject(event.target.value)
            setTimeout(() => {
                formValidation(event)
            }, 100);
        } else if (event.target.name === 'to') {
            setTo(event.target.value)
            setTimeout(() => {
                formValidation(event)
            }, 100);
        } else {
            setBody(event.target.value)
            setTimeout(() => {
                formValidation(event)
            }, 100);
        }
    }

    // Separate validation function for subject field
    const validateSubject = (value) => {
        if (value.trim() === "") {
            setSubjectError("This field is required");
            return false;
        } else {
            setSubjectError("");
            return true;
        }
    };

    // Separate validation function for to field
    const validateTo = (value) => {
        if (value.trim() === "") {
            setToError("This field is required");
            return false;
        } else {
            setToError("");
            return true;
        }
    };

    // Separate validation function for body field
    const validateBody = (value) => {
        if (value.trim() === "") {
            setBodyError("This field is required");
            return false;
        } else {
            setBodyError("");
            return true;
        }
    };

    const formValidation = (event) => {
        let isValidate = true;

        switch (event.target.name) {
            case 'subject':
                isValidate = validateSubject(event.target.value);
                break;
            case 'to':
                isValidate = validateTo(event.target.value);
                break;
            case 'body':
                isValidate = validateBody(event.target.value);
                break;
            default:
                break;
        }

        return isValidate;
    }

    const copy = async (e, text) => {
        e.preventDefault();

        if (e.target.className.trim() === '') {
            e.stopPropagation();
            return;
        }
        await navigator.clipboard.writeText(text);
        setTimeout(() => {}, 1500);

        if (e.target.className.trim() === 'body-copy-icon') {
            logEvents(161, 'AI Email Used')
        }
    };

    const logEvents = (type, text) => {
        logEvent({ event_type: type }, dispatch);
        window.Intercom('trackEvent', text)
    }

    const handleSubmit = (event) => {
        // Validate all fields using separate validation functions
        const isSubjectValid = validateSubject(subject);
        const isToValid = validateTo(to);
        const isBodyValid = validateBody(body);

        if (isSubjectValid && isToValid && isBodyValid) {
            if (projectEmail) {
                const lst_of_project_ids = [parseInt(projectDetails.detailsPage.objectId)];
                const payload = {"object_id": lst_of_project_ids, "event_type": 100}
                markAsMailed(payload, dispatch, 'projects', navigate, location)
            }
            if (companyEmail) {
                const lst_of_company_ids = props.objectType === 'company' ? [parseInt(props.entity.objectId)] : [parseInt(props.company.companyId)];
                const payload = {"object_id": lst_of_company_ids, "event_type": 100}
                markAsMailed(payload, dispatch, 'companies', null, null, projectDetails)
            }
            if (peopleEmail) {
                const lst_of_person_ids = [parseInt(props.entity.person_id)];
                const payload = {"object_id": lst_of_person_ids, "event_type": 100}
                markAsMailed(payload, dispatch, 'people', null, null, projectDetails)
            }
            logEvents(161, 'AI Email Used')
        } else {
            event.preventDefault();
        }
    }

    return (
        <Modal isOpen={props.isOpen} toggle={(e) => toggleModal(e)} id="launchEmailModal">
            <ModalHeader className="blueHeaderBg mb-3">
                Email
                <button type="button" className="close" aria-label="Close" onClick={(e) => { toggleModal(e) }}><span aria-hidden="true">×</span></button>
            </ModalHeader>
            <div className="emailFormCnt">
                <form>
                    <div className="email-form">
                        <Row className="mb-3">
                            <Col className="email-input mb-3 border-bottom pb-3" md="12">
                                <label>Prompt</label>
                                <Dropdown isOpen={dropDownOpen['promptDropdown']} toggle={() => toggleDropdown('promptDropdown')}>
                                    <DropdownToggle caret>{emailPrompts.find(prompt => prompt.prompt_id === dropDownValuePrompt)?.title || ''}</DropdownToggle>
                                    <DropdownMenu>
                                        {emailPrompts.map((optionItem) => {
                                            return <DropdownItem value={optionItem.prompt_id} onClick={() => handleDropdownLabel('promptDropdown', optionItem)} >{optionItem.title}</DropdownItem>
                                        })}
                                    </DropdownMenu>
                                </Dropdown>
                            </Col>
                            <Col className="email-input mb-3 mt-4" md="12">
                                <label>To</label>
                                <Link onClick={(e) => {copy(e, to)}}><span className={`copy-icon ${toError ? "disabled" : ""}`} data-tooltip-id="my-to-copy" data-tooltip-hidden={!settings.preferences.hint_text}></span></Link>
                                <Input type="text" value={to} name="to" onChange={handleInputChange} />
                                {toError ? <p className="input-error-text">{toError}</p> : ""}
                            </Col>
                            <Col className="email-input mb-3" md="12">
                                <label>Subject</label>
                                <Link onClick={(e) => {copy(e, subject)}}><span className={`copy-icon ${subjectError ? "disabled" : ""}`} data-tooltip-id="my-subject-copy" data-tooltip-hidden={!settings.preferences.hint_text}></span></Link>
                                <Input type="text" value={subject} name="subject" onChange={handleInputChange} readOnly={isSubjectLoading} />
                                {subjectError ? <p className="input-error-text">{subjectError}</p> : ""}
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col className="email-input mb-3" md="12">
                                <label>Body</label>
                                <Link onClick={(e) => {copy(e, body)}}><span className={`body-copy-icon ${bodyError ? "disabled" : ""}`} data-tooltip-id="my-body-copy" data-tooltip-hidden={!settings.preferences.hint_text}></span></Link>
                                <Input type="textarea" value={body} name="body" rows={10} onChange={handleInputChange} readOnly={isBodyLoading} />
                                {bodyError ? <p className="error-text">{bodyError}</p> : ""}
                            </Col>
                            <ReactTooltip id="my-to-copy" place="bottom" type='dark' effect="solid">
                                <p className="tool-tip-sidebar">Copy</p>
                            </ReactTooltip>
                            <ReactTooltip id="my-subject-copy" place="bottom" type='dark' effect="solid">
                                <p className="tool-tip-sidebar">Copy</p>
                            </ReactTooltip>
                            <ReactTooltip id="my-body-copy" place="bottom" type='dark' effect="solid">
                                <p className="tool-tip-sidebar">Copy</p>
                            </ReactTooltip>
                        </Row>
                        <Row className="mb-3">
                            <Col md="8">
                                <label className="m-1">Select writing style</label>
                                <p className="mb-0 pt-1">
                                    <fieldset className="form-group mb-0 radio-buttons">
                                        <FormGroup check>
                                            <Input type="radio" id="formal" name="style" checked={formal} onChange={() => setIsFormal(true)} />
                                            <Label check for="formal">
                                                Formal
                                            </Label>
                                        </FormGroup>
                                        <FormGroup check>
                                            <Input type="radio" id="casual" name="style" checked={!formal} onChange={() => setIsFormal(false)} />
                                            <Label check for="casual">
                                                Casual
                                            </Label>
                                        </FormGroup>
                                    </fieldset>
                                </p>
                            </Col>
                            <Col className="email-input" md="4">
                                <div className="check-box-button">
                                    <Input
                                        id={'project-email'}
                                        type="checkbox"
                                        checked={projectEmail}
                                        onChange={() => setProjectEmail(!projectEmail)}
                                    />
                                    <Label className="form-check-label" for={'project-email'}>Mark project as mailed</Label>
                                </div>
                                <div className="check-box-button">
                                    <Input
                                        id={'company-email'}
                                        type="checkbox"
                                        checked={companyEmail}
                                        onChange={() => setCompanyEmail(!companyEmail)}
                                    />
                                    <Label className="form-check-label" for={'company-email'}>Mark company as mailed</Label>
                                </div>
                                <div className="check-box-button">
                                    <Input
                                        id={'people-email'}
                                        type="checkbox"
                                        checked={peopleEmail}
                                        onChange={() => setPeopleEmail(!peopleEmail)}
                                        disabled={props.objectType === 'company' ? true : false}
                                    />
                                    <Label className="form-check-label" for={'people-email'}>Mark person as mailed</Label>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </form>
            </div>
            <ModalFooter className="d-flex justify-content-between">
                <div className="d-inline-flex justify-content-start">
                    <a href={!subjectError && !bodyError && !toError ? `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}` : 'javascript:void(0)'}>
                        <Button id="primaryBtn" disabled={subjectError || bodyError || toError} className="primary-btn btn-save-cancel me-3" type="submit" autoFocus={true} onClick={(e) => { handleSubmit(e) }}>Open Email</Button>
                    </a>
                    <Button className="outline-btn btn-save-cancel" onClick={(e) => { toggleModal(e) }}>Cancel</Button>
                </div>
            </ModalFooter>
        </Modal>
    )
}
export default LaunchEmailModal;
